<script setup lang="ts">
import { computed } from 'vue';
import { useMenuImageUrl } from '@shared/app/composables/useMenuImageUrl';
import { useRecipeImageUrl } from '@shared/app/composables/useRecipeImageUrl';
import { useFetch } from '#app/composables/fetch';

withDefaults(
  // NOTE: レシピレイアウトで使用する場合はタイトル部分の見た目が微妙に違う
  // FIXME: このために処理が増えてるので、できれば統一したい
  defineProps<{
    titleStyle?: 'default' | 'recipe';
  }>(),
  {
    titleStyle: 'default',
  },
);

const { createMenuMainImageUrl } = useMenuImageUrl();
const { createRecipeMainImageUrl } = useRecipeImageUrl();

const { data: menus } = await useFetch('/api/kiwi_user/menus/latest');

const gridItemClasses = [
  'row-start-1 row-end-2 col-start-1 col-end-3 md:row-start-1 md:row-end-3 md:col-start-1 md:col-span-2',
  'row-start-2 row-end-3 col-start-1 col-end-2 md:row-start-1 md:row-end-2 md:col-start-3 md:col-end-3',
  'row-start-2 row-end-3 col-start-2 col-end-3 md:row-start-1 md:row-end-2 md:col-start-4 md:col-end-4',
  'row-start-3 row-end-4 col-start-1 col-end-2 md:row-start-2 md:row-end-3 md:col-start-3 md:col-end-3',
  'row-start-3 row-end-4 col-start-2 col-end-3 md:row-start-2 md:row-end-3 md:col-start-4 md:col-end-4',
];

const transformedMenus = computed(() => {
  return (menus.value ?? []).map((menu) => {
    const recipes = menu.recipes.map((recipe, recipeIndex) => {
      const mainImage = (() => {
        // NOTE: 1つ目のレシピについては、献立のmain_imageがあればそれを使い、なければレシピ自体のmain_imageを使う
        if (recipeIndex === 0 && menu.main_image) {
          return createMenuMainImageUrl({
            menuId: menu.id,
            filename: menu.main_image,
            w: 480,
            h: 300,
          });
        }

        return createRecipeMainImageUrl({
          recipeId: recipe.id,
          filename: recipe.main_image,
          w: 480,
          h: 300,
        });
      })();

      return {
        ...recipe,
        main_image: mainImage,
      };
    });

    return {
      ...menu,
      recipes,
    };
  });
});

// NOTE: プレースホルダーの数を返す（レシピが5件未満の場合、5件になるまでプレースホルダーを表示）
function getPlaceholderCount(recipeCount: number): number {
  return Math.max(0, 5 - recipeCount);
}
</script>

<template>
  <div class="bg-white border border-b-2 border-cream-400 rounded">
    <h2
      v-if="titleStyle === 'default'"
      class="py-3 px-4 font-bold bg-cream-300 border-b border-b-cream-400 mb-4"
    >
      ダイエット献立
    </h2>
    <h2 v-else class="px-5 pt-4 pb-2 font-bold text-xl text-brown-300">
      ダイエット献立
      <div class="border-b border-gray-300 mt-3 mb-1" />
    </h2>

    <ul class="list-none px-5">
      <li
        v-for="menu in transformedMenus"
        :key="menu.old_identifier"
        class="[&:not(:last-child)]:border-b border-gray-300 pt-1 pb-3 mb-3"
      >
        <NuxtLink
          :to="`/public/article/menu/${menu.old_identifier}`"
          class="block overflow-auto hover:opacity-70 duration-300"
        >
          <h3 class="font-bold text-2xl mb-0.5 text-gray-500">
            {{ menu.title }}
          </h3>

          <div class="mb-2 flex items-baseline text-lg">
            <p class="mr-2 flex items-baseline">
              <img
                src="~/assets/img/fire.png"
                class="mr-1 relative top-[3px] w-5"
              />
              <span class="font-bold text-pink-400">{{ menu.energy }}</span>
              <span>kcal</span>
            </p>

            <span class="text-brown-300">
              <span class="font-dietplus-runes before:content-['\ea2b'] mr-1" />
              <b>{{ menu.recipes.length }}</b
              >品の献立</span
            >
          </div>

          <ul
            class="grid grid-cols-2 grid-rows-[auto_auto_auto] md:grid-cols-4 md:grid-rows-2 gap-4"
          >
            <li
              v-for="(recipe, i) in menu.recipes"
              :key="recipe.id"
              :class="gridItemClasses[i]"
            >
              <div class="block relative">
                <img
                  :src="recipe.main_image"
                  class="mb-2 rounded w-full"
                  data-testid="recipe-image"
                />
                <span
                  v-if="recipe.recipe_category_root"
                  class="absolute left-0 bottom-0 text-white bg-black/40 text-sm p-1 rounded-bl"
                  >{{ recipe.recipe_category_root.name }}</span
                >
              </div>
              <h4 class="font-bold">{{ recipe.title }}</h4>
            </li>

            <li
              v-for="i in getPlaceholderCount(menu.recipes.length)"
              :key="`placeholder-${i}`"
              :class="gridItemClasses[menu.recipes.length + i - 1]"
            >
              <div class="mb-2 w-full aspect-[225/105] overflow-hidden">
                <AtomsPlaceholderImage class="w-full h-full object-cover" />
              </div>
            </li>
          </ul>
        </NuxtLink>
      </li>
    </ul>

    <div class="text-right pb-3 px-4">
      <NuxtLink
        to="/public/article/menu/?order=rank"
        class="text-brown-300 text-lg"
        >すべての献立を見る</NuxtLink
      >
    </div>
  </div>
</template>

<style scoped>
@reference '~/assets/css/main.css';
:deep(.menu-needle-horizontal) {
  @apply list-none p-4 clear-both border-b border-gray-300;
}

:deep(.menu-needle-horizontal > a) {
  @apply block overflow-auto hover:opacity-70 duration-300;
}

:deep(.menu-title) {
  @apply text-xl font-bold;
}

:deep(.energy) {
  @apply mr-2;
}

:deep(.energy i) {
  @apply inline-block;
  background-image: url('~/assets/img/fire.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: 0 3px;
}

:deep(.energy i::before) {
  @apply w-6 h-6 inline-block;
  content: '';
}

:deep(.energy b) {
  @apply text-lg;
}

:deep(.heading) {
  @apply mb-2;
}

:deep(.main-recipe) {
  @apply mb-5 md:mb-0 md:w-1/2 md:float-left pr-5;
}

:deep(.photo) {
  @apply relative;
}

:deep(.sub-recipes) {
  @apply md:w-1/2 md:ml-[50%];
}

:deep(.recipes) {
  @apply flex flex-wrap items-start;
}

:deep(.recipe-item) {
  @apply w-1/2;
}

:deep(.recipe-item:nth-child(odd)) {
  @apply pr-4;
}

:deep(.recipe-item:nth-child(even)) {
  @apply pr-4;
}

:deep(.recipe-thumbnail) {
  @apply relative;
}

:deep(.recipe-category) {
  @apply absolute left-0 bottom-0 text-white bg-black/40 text-sm p-1;
}

:deep(.recipe-thumbnail img) {
  @apply mb-1;
}

:deep(.recipe-title) {
  @apply h-14 font-bold;
}

:deep(.placeholder .recipe-thumbnail img) {
  @apply h-[70px] mx-auto;
}
</style>
